/** Bounded mathematical expressions shared by calculated sensors and alarm rules.
 * The source is parsed into an AST. It never becomes JavaScript or accesses a
 * database, object property, browser API or network. */
export type ExpressionMode = 'expression' | 'program'
export type ExpressionValue = number | boolean | null
export type ExpressionResultType = 'number' | 'boolean' | 'unknown'
export interface CompiledMonitoringExpression {
  references: string[]
  resultType: ExpressionResultType
  evaluate(values: Record<string, number | null | undefined>): ExpressionValue
}
export class MonitoringExpressionError extends Error {
  constructor(message: string) { super(message); this.name = 'MonitoringExpressionError' }
}

export interface MathLibraryEntry {
  name: string
  category: 'Arithmetic' | 'Statistics' | 'Angles' | 'Logic'
  signature: string
  description: string
  example: string
}
export const MONITORING_MATH_LIBRARY: MathLibraryEntry[] = [
  { name: 'ABS', category: 'Arithmetic', signature: 'ABS(x)', description: 'Absolute magnitude.', example: 'ABS(A)' },
  { name: 'SQRT', category: 'Arithmetic', signature: 'SQRT(x)', description: 'Square root. Negative inputs produce a gap.', example: 'SQRT(A^2 + B^2)' },
  { name: 'POW', category: 'Arithmetic', signature: 'POW(x, power)', description: 'Raise a value to a power; also written x ^ power.', example: 'POW(A, 2)' },
  { name: 'HYPOT', category: 'Arithmetic', signature: 'HYPOT(x, y, …)', description: 'Resultant magnitude from 2 to 16 components.', example: 'HYPOT(A, B)' },
  { name: 'ROUND', category: 'Arithmetic', signature: 'ROUND(x, digits)', description: 'Round to −12 through 12 decimal places; ties away from zero.', example: 'ROUND(A, 3)' },
  { name: 'FLOOR', category: 'Arithmetic', signature: 'FLOOR(x)', description: 'Round down to the nearest integer.', example: 'FLOOR(A)' },
  { name: 'CEIL', category: 'Arithmetic', signature: 'CEIL(x)', description: 'Round up to the nearest integer.', example: 'CEIL(A)' },
  { name: 'SIGN', category: 'Arithmetic', signature: 'SIGN(x)', description: 'Return −1, 0 or 1 according to the sign.', example: 'SIGN(A)' },
  { name: 'MOD', category: 'Arithmetic', signature: 'MOD(x, divisor)', description: 'Remainder with the divisor’s sign.', example: 'MOD(A, 360)' },
  { name: 'CLAMP', category: 'Arithmetic', signature: 'CLAMP(x, lower, upper)', description: 'Limit a value to a defined range.', example: 'CLAMP(A, 0, 100)' },
  { name: 'LN', category: 'Arithmetic', signature: 'LN(x)', description: 'Natural logarithm of a positive value.', example: 'LN(A)' },
  { name: 'LOG', category: 'Arithmetic', signature: 'LOG(x, base)', description: 'Logarithm; base 10 when the second argument is omitted.', example: 'LOG(A, 10)' },
  { name: 'EXP', category: 'Arithmetic', signature: 'EXP(x)', description: 'Exponential e raised to x.', example: 'EXP(A)' },
  { name: 'MIN', category: 'Statistics', signature: 'MIN(x, y, …)', description: 'Minimum of 2 to 16 simultaneous values.', example: 'MIN(A, B)' },
  { name: 'MAX', category: 'Statistics', signature: 'MAX(x, y, …)', description: 'Maximum of 2 to 16 simultaneous values.', example: 'MAX(A, B)' },
  { name: 'SUM', category: 'Statistics', signature: 'SUM(x, y, …)', description: 'Sum of 2 to 16 simultaneous values. Does not aggregate a time window.', example: 'SUM(A, B)' },
  { name: 'MEAN', category: 'Statistics', signature: 'MEAN(x, y, …)', description: 'Arithmetic mean of 2 to 16 simultaneous values. AVERAGE is an alias.', example: 'MEAN(A, B)' },
  { name: 'MEDIAN', category: 'Statistics', signature: 'MEDIAN(x, y, …)', description: 'Median of 2 to 16 simultaneous values.', example: 'MEDIAN(A, B, C)' },
  { name: 'STDEV', category: 'Statistics', signature: 'STDEV(x, y, …)', description: 'Sample standard deviation of 2 to 16 simultaneous values.', example: 'STDEV(A, B, C)' },
  { name: 'SIN', category: 'Angles', signature: 'SIN(radians)', description: 'Sine. Trigonometric inputs are radians.', example: 'SIN(RADIANS(A))' },
  { name: 'COS', category: 'Angles', signature: 'COS(radians)', description: 'Cosine. Trigonometric inputs are radians.', example: 'COS(RADIANS(A))' },
  { name: 'TAN', category: 'Angles', signature: 'TAN(radians)', description: 'Tangent. Trigonometric inputs are radians.', example: 'TAN(RADIANS(A))' },
  { name: 'ASIN', category: 'Angles', signature: 'ASIN(x)', description: 'Inverse sine in radians; x must lie between −1 and 1.', example: 'DEGREES(ASIN(A))' },
  { name: 'ACOS', category: 'Angles', signature: 'ACOS(x)', description: 'Inverse cosine in radians; x must lie between −1 and 1.', example: 'DEGREES(ACOS(A))' },
  { name: 'ATAN', category: 'Angles', signature: 'ATAN(x)', description: 'Inverse tangent in radians.', example: 'DEGREES(ATAN(A))' },
  { name: 'ATAN2', category: 'Angles', signature: 'ATAN2(y, x)', description: 'Angle of a vector in radians, with y first and x second.', example: 'DEGREES(ATAN2(B, A))' },
  { name: 'RADIANS', category: 'Angles', signature: 'RADIANS(degrees)', description: 'Convert degrees to radians.', example: 'RADIANS(A)' },
  { name: 'DEGREES', category: 'Angles', signature: 'DEGREES(radians)', description: 'Convert radians to degrees.', example: 'DEGREES(A)' },
  { name: 'IF', category: 'Logic', signature: 'IF(condition, trueValue, falseValue)', description: 'Use one branch based on a boolean condition. Unknown conditions remain unknown.', example: 'IF(B = 0, 0, A / B)' },
  { name: 'AND', category: 'Logic', signature: 'AND(condition, condition, …)', description: 'True when all conditions are true. An unknown argument remains unknown.', example: 'AND(A > 2.6, B > 30)' },
  { name: 'OR', category: 'Logic', signature: 'OR(condition, condition, …)', description: 'True when any condition is true. An unknown argument remains unknown.', example: 'OR(A > 2.6, B > 30)' },
  { name: 'NOT', category: 'Logic', signature: 'NOT(condition)', description: 'Reverse a known boolean result.', example: 'NOT(A < 0)' },
]

type Node = { kind: 'literal'; value: number | boolean; resultType: ExpressionResultType }
  | { kind: 'reference'; name: string; resultType: ExpressionResultType }
  | { kind: 'local'; name: string; resultType: ExpressionResultType }
  | { kind: 'unary'; op: '+' | '-'; child: Node; resultType: ExpressionResultType }
  | { kind: 'binary'; op: string; left: Node; right: Node; resultType: ExpressionResultType }
  | { kind: 'call'; name: string; args: Node[]; resultType: ExpressionResultType }
interface Token { text: string; at: number; reference?: string }
const arities: Record<string, [number, number]> = {
  ABS: [1, 1], SQRT: [1, 1], POW: [2, 2], HYPOT: [2, 16], ROUND: [1, 2], FLOOR: [1, 1],
  CEIL: [1, 1], SIGN: [1, 1], MOD: [2, 2], CLAMP: [3, 3], LN: [1, 1], LOG: [1, 2],
  LOG10: [1, 1], EXP: [1, 1], MIN: [2, 16], MAX: [2, 16], SUM: [2, 16], MEAN: [2, 16],
  AVERAGE: [2, 16], MEDIAN: [2, 16], STDEV: [2, 16], SIN: [1, 1], COS: [1, 1], TAN: [1, 1],
  ASIN: [1, 1], ACOS: [1, 1], ATAN: [1, 1], ATAN2: [2, 2], RADIANS: [1, 1], DEGREES: [1, 1],
  IF: [3, 3], AND: [2, 16], OR: [2, 16], NOT: [1, 1],
}
function tokenize(source: string): Token[] {
  const result: Token[] = []
  let at = 0
  while (at < source.length) {
    if (/\s/.test(source[at]!)) { at++; continue }
    const start = at, rest = source.slice(at)
    if (source[at] === '[') {
      const close = source.indexOf(']', at + 1)
      if (close < 0) throw new MonitoringExpressionError('Close the channel reference with ].')
      const reference = source.slice(at + 1, close)
      if (reference.length > 512 || !/^[^|,\s\[\];]+\|[^|,\s\[\];]+$/.test(reference)) {
        throw new MonitoringExpressionError('Write channel references as [station_id|channel_name].')
      }
      result.push({ text: source.slice(at, close + 1), at, reference }); at = close + 1
    } else {
      const match = /^(?:(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?|[A-Za-z_][A-Za-z0-9_]*|>=|<=|==|!=|<>|[+\-*/^(),;=<>])/.exec(rest)
      if (!match) throw new MonitoringExpressionError('Unsupported symbol at character ' + (at + 1) + '.')
      result.push({ text: match[0], at }); at += match[0].length
    }
    if (result.length > 600) throw new MonitoringExpressionError('Use no more than 600 formula terms.')
    if (at === start) throw new MonitoringExpressionError('The formula could not be read.')
  }
  return result
}

export function compileMonitoringExpression(source: string, options: { mode?: ExpressionMode } = {}): CompiledMonitoringExpression {
  if (typeof source !== 'string' || !source.trim()) throw new MonitoringExpressionError('Enter a formula.')
  if (source.length > 4000) throw new MonitoringExpressionError('Use 4,000 characters or fewer.')
  const mode = options.mode ?? 'expression'
  if (mode !== 'expression' && mode !== 'program') throw new MonitoringExpressionError('Choose Formula or Code mode.')
  const tokens = tokenize(source)
  let cursor = 0, depth = 0
  const references = new Set<string>(), locals = new Map<string, ExpressionResultType>()
  const assignments: { name: string; node: Node }[] = []
  const peek = () => tokens[cursor]?.text
  const take = () => tokens[cursor++]!
  const expect = (text: string) => {
    if (peek() !== text) throw new MonitoringExpressionError('Expected "' + text + '"' + (tokens[cursor] ? ' at character ' + (tokens[cursor]!.at + 1) : '') + '.')
    take()
  }
  const nested = (parse: () => Node): Node => {
    if (++depth > 32) throw new MonitoringExpressionError('This formula is nested too deeply.')
    const result = parse(); depth--; return result
  }
  const requireType = (node: Node, type: ExpressionResultType, context: string) => {
    if (node.resultType !== type && node.resultType !== 'unknown') throw new MonitoringExpressionError(context + ' needs ' + (type === 'boolean' ? 'a condition.' : 'a numeric value.'))
  }
  const primary = (): Node => nested(() => {
    const token = tokens[cursor]
    if (!token) throw new MonitoringExpressionError('The formula is incomplete.')
    if (token.reference !== undefined) {
      take(); references.add(token.reference)
      return { kind: 'reference', name: token.reference, resultType: 'number' }
    }
    if (token.text === '(') { take(); const node = expression(); expect(')'); return node }
    if (/^(?:\d|\.)/.test(token.text)) {
      take(); const value = Number(token.text)
      if (!Number.isFinite(value)) throw new MonitoringExpressionError('Use finite numeric constants.')
      return { kind: 'literal', value, resultType: 'number' }
    }
    if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(token.text)) {
      take(); const name = token.text.toUpperCase()
      if (peek() === '(') {
        const arity = arities[name]
        if (!arity) throw new MonitoringExpressionError('Unknown function ' + token.text + '. Open the math library for supported functions.')
        take(); const args: Node[] = []
        if (peek() !== ')') { args.push(expression()); while (peek() === ',') { take(); args.push(expression()) } }
        expect(')')
        if (args.length < arity[0] || args.length > arity[1]) throw new MonitoringExpressionError(name + ' needs ' + (arity[0] === arity[1] ? arity[0] : arity[0] + ' to ' + arity[1]) + ' arguments.')
        let resultType: ExpressionResultType = 'number'
        if (name === 'IF') {
          requireType(args[0]!, 'boolean', 'IF condition')
          resultType = args[1]!.resultType === args[2]!.resultType ? args[1]!.resultType : 'unknown'
        } else if (name === 'AND' || name === 'OR' || name === 'NOT') {
          for (const arg of args) requireType(arg, 'boolean', name)
          resultType = 'boolean'
        } else for (const arg of args) requireType(arg, 'number', name)
        return { kind: 'call', name, args, resultType }
      }
      if (name === 'PI' || name === 'E') return { kind: 'literal', value: name === 'PI' ? Math.PI : Math.E, resultType: 'number' }
      if (name === 'TRUE' || name === 'FALSE') return { kind: 'literal', value: name === 'TRUE', resultType: 'boolean' }
      if (name === 'LET' || name === 'RETURN' || name === 'CONST') throw new MonitoringExpressionError('Use let assignments and a final return in Code mode.')
      if (locals.has(token.text)) return { kind: 'local', name: token.text, resultType: locals.get(token.text)! }
      references.add(token.text)
      return { kind: 'reference', name: token.text, resultType: 'number' }
    }
    throw new MonitoringExpressionError('Expected a value at character ' + (token.at + 1) + '.')
  })
  const binary = (op: string, left: Node, right: Node, resultType: ExpressionResultType): Node => {
    requireType(left, 'number', 'Operator ' + op); requireType(right, 'number', 'Operator ' + op)
    return { kind: 'binary', op, left, right, resultType }
  }
  const power = (): Node => {
    const left = primary()
    if (peek() !== '^') return left
    take(); return binary('^', left, unary(), 'number')
  }
  const unary = (): Node => nested(() => {
    if (peek() === '+' || peek() === '-') {
      const op = take().text as '+' | '-', child = unary()
      requireType(child, 'number', 'Unary ' + op)
      return { kind: 'unary', op, child, resultType: 'number' }
    }
    return power()
  })
  const product = (): Node => {
    let node = unary()
    while (peek() === '*' || peek() === '/') { const op = take().text; node = binary(op, node, unary(), 'number') }
    return node
  }
  const sum = (): Node => {
    let node = product()
    while (peek() === '+' || peek() === '-') { const op = take().text; node = binary(op, node, product(), 'number') }
    return node
  }
  const expression = (): Node => {
    const left = sum()
    if (!['>', '>=', '<', '<=', '=', '==', '!=', '<>'].includes(peek() ?? '')) return left
    const op = take().text
    return binary(op, left, sum(), 'boolean')
  }
  let root: Node
  if (mode === 'program') {
    while (peek()?.toLowerCase() === 'let') {
      take(); const name = take()?.text
      if (!name || !/^[a-z_][A-Za-z0-9_]*$/.test(name) || name.length > 40
          || ['PI', 'E', 'TRUE', 'FALSE', 'RETURN', 'LET'].includes(name.toUpperCase()) || Object.hasOwn(arities, name.toUpperCase()) || locals.has(name)) {
        throw new MonitoringExpressionError('Use a unique local name starting with a lowercase letter or underscore.')
      }
      expect('='); const node = expression(); expect(';')
      if (references.has(name)) throw new MonitoringExpressionError('Define ' + name + ' before using it.')
      locals.set(name, node.resultType); assignments.push({ name, node })
      if (assignments.length > 12) throw new MonitoringExpressionError('Use no more than 12 local assignments.')
    }
    if (peek()?.toLowerCase() !== 'return') throw new MonitoringExpressionError('End the code with return followed by a formula.')
    take(); root = expression(); if (peek() === ';') take()
  } else {
    // An optional initial = follows spreadsheet formula notation.
    if (peek() === '=') take()
    root = expression()
  }
  if (cursor !== tokens.length) throw new MonitoringExpressionError('Unexpected "' + peek() + '" at character ' + (tokens[cursor]!.at + 1) + '.')
  if (references.size > 32) throw new MonitoringExpressionError('Use no more than 32 source references.')
  return {
    references: [...references].sort(), resultType: root.resultType,
    evaluate(values) {
      const localValues = new Map<string, ExpressionValue>()
      const calculate = (node: Node): ExpressionValue => {
        if (node.kind === 'literal') return node.value
        if (node.kind === 'local') return localValues.get(node.name) ?? null
        if (node.kind === 'reference') {
          const value = Object.hasOwn(values, node.name) ? values[node.name] : null
          return typeof value === 'number' && Number.isFinite(value) ? value : null
        }
        if (node.kind === 'unary') { const value = calculate(node.child); return typeof value === 'number' ? (node.op === '-' ? -value : value) : null }
        if (node.kind === 'binary') {
          const left = calculate(node.left), right = calculate(node.right)
          if (typeof left !== 'number' || typeof right !== 'number') return null
          let value: number | boolean
          switch (node.op) {
            case '+': value = left + right; break
            case '-': value = left - right; break
            case '*': value = left * right; break
            case '/': value = right === 0 ? NaN : left / right; break
            case '^': value = left ** right; break
            case '>': return left > right
            case '>=': return left >= right
            case '<': return left < right
            case '<=': return left <= right
            case '=': case '==': return left === right
            default: return left !== right
          }
          return Number.isFinite(value) ? value : null
        }
        if (node.name === 'IF') {
          const condition = calculate(node.args[0]!)
          return typeof condition === 'boolean' ? calculate(node.args[condition ? 1 : 2]!) : null
        }
        const args = node.args.map(calculate)
        if (args.some(value => value === null)) return null
        if (node.name === 'AND' || node.name === 'OR' || node.name === 'NOT') {
          if (!args.every(value => typeof value === 'boolean')) return null
          return node.name === 'AND' ? args.every(Boolean) : node.name === 'OR' ? args.some(Boolean) : !args[0]
        }
        if (!args.every(value => typeof value === 'number')) return null
        return finiteMath(node.name, args as number[])
      }
      for (const assignment of assignments) localValues.set(assignment.name, calculate(assignment.node))
      return calculate(root)
    },
  }
}

function finiteMath(name: string, args: number[]): number | null {
  const [a, b, c] = args as [number, number, number]
  let value: number
  switch (name) {
    case 'ABS': value = Math.abs(a); break
    case 'SQRT': value = Math.sqrt(a); break
    case 'POW': value = a ** b; break
    case 'HYPOT': value = Math.hypot(...args); break
    case 'ROUND': {
      const digits = b ?? 0
      if (!Number.isInteger(digits) || Math.abs(digits) > 12) return null
      const factor = 10 ** digits, scaled = Math.abs(a) * factor
      value = Number.isFinite(scaled) ? Math.sign(a) * Math.round(scaled) / factor : NaN
      break
    }
    case 'FLOOR': value = Math.floor(a); break
    case 'CEIL': value = Math.ceil(a); break
    case 'SIGN': value = Math.sign(a); break
    case 'MOD': value = b === 0 ? NaN : a - b * Math.floor(a / b); break
    case 'CLAMP': value = b > c ? NaN : Math.max(b, Math.min(c, a)); break
    case 'LN': value = a > 0 ? Math.log(a) : NaN; break
    case 'LOG': {
      const base = b ?? 10
      value = a > 0 && base > 0 && base !== 1 ? Math.log(a) / Math.log(base) : NaN
      break
    }
    case 'LOG10': value = a > 0 ? Math.log10(a) : NaN; break
    case 'EXP': value = Math.exp(a); break
    case 'MIN': value = Math.min(...args); break
    case 'MAX': value = Math.max(...args); break
    case 'SUM': value = args.reduce((sum, n) => sum + n, 0); break
    case 'MEAN': case 'AVERAGE': value = args.reduce((sum, n) => sum + n / args.length, 0); break
    case 'MEDIAN': {
      const sorted = [...args].sort((x, y) => x - y), middle = Math.floor(sorted.length / 2)
      value = sorted.length % 2 ? sorted[middle]! : sorted[middle - 1]! / 2 + sorted[middle]! / 2
      break
    }
    case 'STDEV': {
      const scale = Math.max(...args.map(Math.abs))
      if (scale === 0) { value = 0; break }
      const scaled = args.map(n => n / scale), mean = scaled.reduce((sum, n) => sum + n / args.length, 0)
      value = Math.sqrt(scaled.reduce((sum, n) => sum + (n - mean) ** 2, 0) / (args.length - 1)) * scale
      break
    }
    case 'SIN': value = Math.sin(a); break
    case 'COS': value = Math.cos(a); break
    case 'TAN': value = Math.abs(Math.cos(a)) < 1e-15 ? NaN : Math.tan(a); break
    case 'ASIN': value = Math.asin(a); break
    case 'ACOS': value = Math.acos(a); break
    case 'ATAN': value = Math.atan(a); break
    case 'ATAN2': value = a === 0 && b === 0 ? NaN : Math.atan2(a, b); break
    case 'RADIANS': value = a * (Math.PI / 180); break
    case 'DEGREES': value = a * (180 / Math.PI); break
    default: return null
  }
  return Number.isFinite(value) ? value : null
}
